import config from '../config';
import selectedEnvironment from '../env';

const baseURL = config[selectedEnvironment].API_URL;
const Projects_API_URL = `${baseURL}/Segmentations`;

const getDownloadFileName = (response, fallbackFileName) => {
    const contentDisposition = response.headers.get('content-disposition') || '';
    const utfFileNameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utfFileNameMatch?.[1]) {
        return decodeURIComponent(utfFileNameMatch[1]);
    }

    const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    return fileNameMatch?.[1] || fallbackFileName;
};

const downloadFileFromResponse = async (response, fallbackFileName) => {
    const fileContent = await response.blob();
    const fileUrl = window.URL.createObjectURL(fileContent);
    const link = document.createElement('a');
    link.href = fileUrl;
    link.setAttribute('download', getDownloadFileName(response, fallbackFileName));
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(fileUrl);
};

const getProjects = async (caseId, auth) => {
    return fetch(`${Projects_API_URL}/ByCaseId/${caseId}`, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${auth}`,
            'Content-Type': 'application/json',
        },
    });
};

const createProject = async (payload, auth) => {
    return fetch(`${Projects_API_URL}/Create`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${auth}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });
};

const updateProject = async (payload, auth) => {
    return fetch(`${Projects_API_URL}/Update`, {
        method: 'PUT',
        headers: {
            Authorization: `Bearer ${auth}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });
};

const deleteProject = async (id, caseId, auth) => {
    return fetch(`${Projects_API_URL}/Delete/${id}/${caseId}`, {
        method: 'DELETE',
        headers: {
            Authorization: `Bearer ${auth}`,
            'Content-Type': 'application/json',
        },
    });
};

const generateProjectsPdf = async (caseId, auth) => {
    const response = await fetch(`${Projects_API_URL}/GeneratePdf`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${auth}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ CaseId: caseId }),
    });

    if (response.ok) {
        await downloadFileFromResponse(response, `Projects_${caseId}.pdf`);
    }

    return response;
};

const generateProjectsExcel = async (caseId, auth) => {
    const response = await fetch(`${Projects_API_URL}/GenerateExcel`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${auth}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ CaseId: caseId }),
    });

    if (response.ok) {
        await downloadFileFromResponse(response, `Projects_${caseId}.xlsx`);
    }

    return response;
};

/** Assign document(s) to a segmentation (project). Pass only the list relevant to the document type. */
const assignDocuments = async (payload, auth) => {
    return fetch(`${Projects_API_URL}/AssignDocuments`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${auth}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });
};

const removeIncomeFromSegmentation = async (segmentationId, caseId, incomeId, auth) => {
    return fetch(
        `${Projects_API_URL}/RemoveIncome/${segmentationId}/${encodeURIComponent(caseId)}/${incomeId}`,
        {
            method: 'DELETE',
            headers: {
                Authorization: `Bearer ${auth}`,
                'Content-Type': 'application/json',
            },
        }
    );
};

const removeExpenseFromSegmentation = async (segmentationId, caseId, expenseId, auth) => {
    return fetch(
        `${Projects_API_URL}/RemoveExpense/${segmentationId}/${encodeURIComponent(caseId)}/${expenseId}`,
        {
            method: 'DELETE',
            headers: {
                Authorization: `Bearer ${auth}`,
                'Content-Type': 'application/json',
            },
        }
    );
};

const removeJournalEntryFromSegmentation = async (segmentationId, caseId, journalEntryId, auth) => {
    return fetch(
        `${Projects_API_URL}/RemoveJournalEntry/${segmentationId}/${encodeURIComponent(caseId)}/${journalEntryId}`,
        {
            method: 'DELETE',
            headers: {
                Authorization: `Bearer ${auth}`,
                'Content-Type': 'application/json',
            },
        }
    );
};

const removePaymentAcceptanceFromSegmentation = async (segmentationId, caseId, paymentAcceptanceId, auth) => {
    return fetch(
        `${Projects_API_URL}/RemovePaymentAcceptance/${segmentationId}/${encodeURIComponent(caseId)}/${paymentAcceptanceId}`,
        {
            method: 'DELETE',
            headers: {
                Authorization: `Bearer ${auth}`,
                'Content-Type': 'application/json',
            },
        }
    );
};

const ProjectsService = {
    getProjects,
    createProject,
    updateProject,
    deleteProject,
    generateProjectsPdf,
    generateProjectsExcel,
    assignDocuments,
    removeIncomeFromSegmentation,
    removeExpenseFromSegmentation,
    removeJournalEntryFromSegmentation,
    removePaymentAcceptanceFromSegmentation,
};

export default ProjectsService;
